// Dashboard data models based on design spec

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  joinedAt: Date;
  isFirstVisit: boolean;
  guidanceCompleted: boolean;
  lastActiveDate: Date;
  activityMetrics: UserActivityMetrics;
}

export interface GuidanceState {
  enabled: boolean;
  currentStep: number;
  completedSteps: number[];
  dismissed: boolean;
  lastShown: Date;
  completionRate: number;
  userPreferences: GuidancePreferences;
  interactions: GuidanceInteractions;
}

export interface ConnectedRepository {
  id: string;
  name: string;
  fullName: string; // owner/repo
  provider: 'github' | 'gitlab' | 'bitbucket' | 'upload';
  platform?: 'github' | 'gitlab' | 'bitbucket' | 'upload';
  source?: 'git' | 'upload' | 'oauth';
  status: 'healthy' | 'analyzing' | 'issues' | 'error';
  lastAnalysis: Date | null;
  analysisCount: number;
  devScore: number | null;
  maintenanceScore: number | null;
  url: string;
  cloneUrl?: string;
}

export interface RecentAnalysis {
  id: string;
  repositoryId: string;
  repositoryName: string;
  startedAt: Date;
  completedAt: Date | null;
  status: 'complete' | 'in-progress' | 'failed';
  overallScore: number | null;
  issuesFound: number | null;
  summary: string;
  detailUrl: string;
}

export interface DashboardStats {
  totalRepositories: number;
  totalAnalyses: number;
  averageScore: number;
  issuesResolved: number;
}

export interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  highlighted?: boolean;
}

export interface DashboardData {
  user: UserProfile;
  guidance: GuidanceState;
  repositories: ConnectedRepository[];
  recentAnalyses: RecentAnalysis[];
  stats: DashboardStats;
}

// Enhanced tracking interfaces
export interface UserActivityMetrics {
  repositoriesConnected: number;
  analysesCompleted: number;
  lastActiveDate: Date;
  featuresUsed: string[];
  sessionsCount: number;
  totalTimeSpent: number; // milliseconds
  averageSessionDuration: number; // milliseconds
  guidanceInteractions: GuidanceInteractionMetrics;
}

export interface GuidancePreferences {
  autoAdvance: boolean;
  showAnimations: boolean;
  skipOptionalSteps: boolean;
  preferredTooltipPosition: 'auto' | 'top' | 'bottom' | 'left' | 'right';
  enableKeyboardShortcuts: boolean;
  showProgressIndicator: boolean;
}

export interface GuidanceInteractions {
  totalStepsCompleted: number;
  totalTimeSpent: number; // milliseconds
  dismissalCount: number;
  restartCount: number;
  skipCount: number;
  completionCount: number;
  lastInteractionDate: Date;
  stepCompletionTimes: Record<string, number>; // stepId -> time in ms
}

export interface GuidanceInteractionMetrics {
  stepsCompleted: number;
  timeSpent: number; // milliseconds
  dismissalCount: number;
  completionRate: number; // 0-1
  averageStepTime: number; // milliseconds
  mostSkippedSteps: string[];
  preferredCompletionPath: string[];
}

// State persistence interfaces
export interface GuidanceStateUpdate {
  currentStep?: number;
  completedSteps?: number[];
  dismissed?: boolean;
  enabled?: boolean;
  preferences?: Partial<GuidancePreferences>;
  interactions?: Partial<GuidanceInteractions>;
}

export interface ActivityTrackingEvent {
  type:
    | 'feature_used'
    | 'session_start'
    | 'session_end'
    | 'guidance_interaction'
    | 'repository_connected'
    | 'analysis_completed';
  timestamp: Date;
  data?: Record<string, unknown>;
  sessionId?: string;
}

// Default values
export const DEFAULT_GUIDANCE_PREFERENCES: GuidancePreferences = {
  autoAdvance: false,
  showAnimations: true,
  skipOptionalSteps: false,
  preferredTooltipPosition: 'auto',
  enableKeyboardShortcuts: true,
  showProgressIndicator: true,
};

export const DEFAULT_GUIDANCE_INTERACTIONS: GuidanceInteractions = {
  totalStepsCompleted: 0,
  totalTimeSpent: 0,
  dismissalCount: 0,
  restartCount: 0,
  skipCount: 0,
  completionCount: 0,
  lastInteractionDate: new Date(),
  stepCompletionTimes: {},
};

export const DEFAULT_USER_ACTIVITY_METRICS: UserActivityMetrics = {
  repositoriesConnected: 0,
  analysesCompleted: 0,
  lastActiveDate: new Date(),
  featuresUsed: [],
  sessionsCount: 0,
  totalTimeSpent: 0,
  averageSessionDuration: 0,
  guidanceInteractions: {
    stepsCompleted: 0,
    timeSpent: 0,
    dismissalCount: 0,
    completionRate: 0,
    averageStepTime: 0,
    mostSkippedSteps: [],
    preferredCompletionPath: [],
  },
};
